import { useState } from 'react';
import type { ThreadEmail } from '../hooks/useThreadEmails';
import styles from './EmailMessage.module.css';

const TOKEN_KEY = 'foreman-auth-token';

interface EmailMessageProps {
  email: ThreadEmail;
}

async function fetchAttachmentBlob(emailId: number, idx: number): Promise<Blob> {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`/api/auth/emails/${emailId}/attachments/${idx}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return res.blob();
}

async function downloadAttachment(emailId: number, idx: number, filename: string) {
  const blob = await fetchAttachmentBlob(emailId, idx);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'attachment';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Open an attachment in a new browser tab so it can be viewed and printed
 *  (Save-as-PDF or a printer). We reserve the tab synchronously inside the click
 *  gesture so the popup isn't blocked, then point it at the blob once loaded.
 *  PDFs and images render in the tab; formats the browser can't display (e.g.
 *  .docx) will download instead — same as opening any such link. */
async function openAttachment(emailId: number, idx: number) {
  const win = window.open('', '_blank');
  try {
    const blob = await fetchAttachmentBlob(emailId, idx);
    const url = URL.createObjectURL(blob);
    if (win) win.location.href = url;
    else {
      // popup blocked — fall back to a normal download
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    if (win) win.close();
    throw err;
  }
}

function formatTime(epochSec: number): string {
  const d = new Date(epochSec * 1000);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function EmailMessage({ email }: EmailMessageProps) {
  const isInbound = email.direction === 'in';
  const who = isInbound ? email.fromAddr : email.toAddr;
  const [downloading, setDownloading] = useState<number | null>(null);
  const [opening, setOpening] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async (idx: number, filename: string) => {
    setDownloading(idx);
    setDownloadError(null);
    try {
      await downloadAttachment(email.id, idx, filename);
    } catch (err) {
      setDownloadError((err as Error).message);
    } finally {
      setDownloading(null);
    }
  };

  const handleOpen = async (idx: number) => {
    setOpening(idx);
    setDownloadError(null);
    try {
      await openAttachment(email.id, idx);
    } catch (err) {
      setDownloadError((err as Error).message);
    } finally {
      setOpening(null);
    }
  };

  return (
    <div className={`${styles.row} ${isInbound ? styles.in : styles.out}`}>
      <div className={styles.head}>
        <span className={styles.icon} aria-hidden>{isInbound ? '\u{1F4E5}' : '\u{1F4E4}'}</span>
        <span className={styles.label}>
          {isInbound ? 'Email from' : 'Email to'} <strong>{who}</strong>
        </span>
        <span className={styles.time}>{formatTime(email.receivedAt)}</span>
      </div>
      {email.subject && <div className={styles.subject}>{email.subject}</div>}
      {email.textBody && (
        <div className={styles.body}>{truncate(email.textBody, 1200)}</div>
      )}
      {email.attachments.length > 0 && (
        <div className={styles.attachments}>
          {email.attachments.map((a, i) => (
            <span key={i} className={styles.attachmentRow}>
              <button
                type="button"
                className={styles.attachment}
                onClick={() => handleOpen(i)}
                disabled={opening === i}
                title={`Open ${a.filename} in a new tab (view / print / save as PDF)`}
              >
                {opening === i ? '\u{23F3} ' : '\u{1F4CE} '}
                {a.filename}
                <span className={styles.attachmentSize}>
                  {a.size < 1024 * 1024
                    ? ` (${Math.round(a.size / 1024)}KB)`
                    : ` (${(a.size / (1024 * 1024)).toFixed(1)}MB)`}
                </span>
              </button>
              <button
                type="button"
                className={styles.attachmentDownload}
                onClick={() => handleDownload(i, a.filename)}
                disabled={downloading === i}
                title={`Download ${a.filename}`}
                aria-label={`Download ${a.filename}`}
              >
                {downloading === i ? '\u{23F3}' : '\u{2B07}'}
              </button>
            </span>
          ))}
          {downloadError && <span className={styles.downloadError}>{downloadError}</span>}
        </div>
      )}
      <a
        className={styles.viewOriginal}
        href={`/api/auth/emails/${email.id}/html`}
        target="_blank"
        rel="noreferrer"
      >
        View original
      </a>
    </div>
  );
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n) + '…';
}
